/**
 * Tenant Utilities
 *
 * Functions for tenant detection and resolution
 */

const DEFAULT_TENANT_SLUG = "fermata"

/**
 * Extract tenant slug from URL
 * Priority:
 * 1. Query parameter ?tenant=slug (dev mode)
 * 2. Subdomain (e.g., nuvve.gridbox.fermataenergy.io → "nuvve")
 * 3. Default to "fermata"
 */
export function getTenantSlug(): string {
  // Check query parameter first (dev mode)
  const urlParams = new URLSearchParams(window.location.search)
  const tenantParam = urlParams.get("tenant")
  if (tenantParam) {
    return tenantParam
  }

  // Check subdomain
  const hostname = window.location.hostname

  // Skip localhost and IP addresses
  if (hostname === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return DEFAULT_TENANT_SLUG
  }

  // Extract subdomain from hostname
  // Examples:
  // - nuvve.gridbox.fermataenergy.io → "nuvve"
  // - gridbox.fermataenergy.io → default
  // - fermataenergy.io → default
  const parts = hostname.split(".")

  // If we have more than 2 parts, the first part might be the tenant slug
  if (parts.length > 2) {
    const potentialTenant = parts[0]

    // Skip common prefixes that aren't tenant slugs
    if (!["www", "app", "api", "gridbox"].includes(potentialTenant)) {
      return potentialTenant
    }
  }

  // Default tenant
  return DEFAULT_TENANT_SLUG
}

/**
 * Logo path for the current tenant. Uses {@link getTenantSlug} so product hosts
 * like `gridbox.us-staging.fermataenergy.io` resolve to the Fermata logo rather
 * than a missing `/imgs/gridbox/logo.png`.
 */
export function getTenantLogoUrl(): string {
  return `/imgs/${getTenantSlug()}/logo.png`
}

/**
 * Get tenant ID from slug
 * Note: This would typically require an API call to map slug to ID
 * For now, we'll handle this in the context
 */
export function setTenantQueryParam(slug: string): void {
  const url = new URL(window.location.href)
  url.searchParams.set("tenant", slug)
  window.history.replaceState({}, "", url.toString())
}

/**
 * Remove tenant query parameter
 */
export function removeTenantQueryParam(): void {
  const url = new URL(window.location.href)
  url.searchParams.delete("tenant")
  window.history.replaceState({}, "", url.toString())
}
